import { MdsButton, MdsButtonVariant } from "@/design-system/components/button";
import { VerificationStatus } from "@/domains/integrations/verification-status";
import { MDSSection, MDSTextField } from "@/mds";
import { IntegrationsContentResend } from "@/pages/integrations/shared/integrations-resend";
import { StyledSubTitle } from "@/pages/integrations/shared/integrations.styles";
import { useAppStore } from "@/store";
import { EmailSenderObservable } from "@/store/email-senders/EmailSenderObservable";
import { AppStoreEmailSendersStore } from "@/store/email-senders/AppStoreEmailSendersStore";
import { TextMessageSenderObservable } from "@/store/text-message-senders/TextMessageSenderObservable";
import { AppStoreTextMessageSendersStore } from "@/store/text-message-senders/AppStoreTextMessageSendersStore";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { useTimeout } from "usehooks-ts";

type TProps = {
  unVerifiedItem: EmailSenderObservable | TextMessageSenderObservable;
};

export const IntegrationsPageContentForm = observer<TProps>(({ unVerifiedItem }) => {
  const { store } = useAppStore();
  const integrationStore =
    unVerifiedItem instanceof EmailSenderObservable ? store.emailSenders : store.textMessageSenders;

  const {
    isCreating,
    create,
    isVerifying,
    verify,
    isRequestingNewVerificationCode,
    clearUnverifiedItem,
  } = integrationStore;

  const {
    id,
    input,
    isInputValid,
    setInput,
    setVerificationToken,
    verificationStatus,
    verificationToken,
  } = unVerifiedItem;

  const isEmail = unVerifiedItem instanceof EmailSenderObservable;
  const label = isEmail ? "email" : "phone number";
  const awaitVerification = verificationStatus === VerificationStatus.AwaitsVerification;
  const isinValidVerificationToken = verificationStatus === VerificationStatus.Invalid;
  const isOnVerificationStep =
    verificationStatus === VerificationStatus.AwaitsVerification ||
    verificationStatus === VerificationStatus.Invalid;

  const timeout = isEmail
    ? AppStoreEmailSendersStore.VERIFICATION_TIMEOUT
    : AppStoreTextMessageSendersStore.VERIFICATION_TIMEOUT;

  const { isVerificationTimedOut, setIsVerificationTimedOut } = useVerificationTimeout({
    awaitVerification,
    timeout,
  });

  const handleInputChange = (value: string) => {
    setInput(value);
  };

  const handleVerificationTokenChange = (value: string) => {
    setIsVerificationTimedOut(false);
    setVerificationToken(value);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!isOnVerificationStep) {
      create(input);
      return;
    }

    if (!verificationToken) {
      return;
    }

    verify(id, verificationToken);
  };

  return (
    <StyledForm onSubmit={handleSubmit}>
      <StyledSection title={<StyledSubTitle>Add new {label}</StyledSubTitle>}>
        {isEmail ? (
          <MDSTextField
            type="email"
            value={input}
            placeholder={`Enter ${label}`}
            isDisabled={isCreating}
            onChange={handleInputChange}
            isReadOnly={awaitVerification}
            isInvalid={false}
            aria-label={label}
            autoFocus
          />
        ) : (
          <MDSTextField
            type="tel"
            value={input}
            placeholder={`Enter ${label}`}
            pattern="[0-9]*"
            isDisabled={isCreating}
            onChange={handleInputChange}
            isReadOnly={awaitVerification}
            isInvalid={false}
            aria-label={label}
            autoFocus
          />
        )}
        {isOnVerificationStep && (
          <MDSTextField
            isDisabled={isCreating}
            value={verificationToken}
            placeholder="Enter verification code"
            onChange={handleVerificationTokenChange}
            isInvalid={isinValidVerificationToken}
            aria-label="Verification code"
            autoComplete="one-time-code"
            autoFocus
          />
        )}

        <FooterStyles>
          <LeftStyles>
            {isinValidVerificationToken && (
              <ErrorStyles>
                Incorrect verification code.{" "}
                <IntegrationsContentResend integrationStore={integrationStore} isError />
              </ErrorStyles>
            )}
            {isVerificationTimedOut && (
              <TimeoutStyles>
                Didn’t receive {label === "email" ? "an email" : "a text"}?{" "}
                <IntegrationsContentResend integrationStore={integrationStore} />
              </TimeoutStyles>
            )}
          </LeftStyles>
          <RightStyles>
            <MdsButton
              type="reset"
              variant={MdsButtonVariant.Outlined}
              label="Cancel"
              isDisabled={isCreating || isVerifying}
              onClick={clearUnverifiedItem}
            />
            {isOnVerificationStep ? (
              <MdsButton
                type="submit"
                label="Verify"
                isLoading={isVerifying || isRequestingNewVerificationCode}
                variant={MdsButtonVariant.FilledDark}
                isDisabled={!verificationToken || isinValidVerificationToken}
              />
            ) : (
              <MdsButton
                type="submit"
                isLoading={isCreating}
                isDisabled={!isInputValid}
                label="Get verification code"
                variant={MdsButtonVariant.FilledDark}
                onClick={handleSubmit}
              />
            )}
          </RightStyles>
        </FooterStyles>
      </StyledSection>
    </StyledForm>
  );
});

const useVerificationTimeout = ({
  awaitVerification,
  timeout,
}: {
  awaitVerification: boolean;
  timeout: number;
}) => {
  const [isVerificationTimedOut, setIsVerificationTimedOut] = useState(false);

  useTimeout(
    () => {
      setIsVerificationTimedOut(true);
    },
    awaitVerification ? timeout : null
  );

  return { isVerificationTimedOut, setIsVerificationTimedOut };
};

const StyledForm = styled.form(() => ({
  width: "100%",
}));

const StyledSection = styled(MDSSection)(() => ({
  width: "100%",
}));

const FooterStyles = styled.div({
  marginTop: "8px",
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
});

const LeftStyles = styled.div({
  display: "flex",
  gap: "8px",
});

const RightStyles = styled.div({
  display: "flex",
  gap: "8px",
});

const ErrorStyles = styled.div(({ theme }) => ({
  color: theme.colors.red.x500,
  fontSize: theme.fontSizes.xsmall,
  lineHeight: theme.lineHeights.xsmall,
}));

const TimeoutStyles = styled.div(({ theme }) => ({
  color: theme.colors.grey.x500,
  fontSize: theme.fontSizes.xsmall,
  lineHeight: theme.lineHeights.xsmall,
}));
